import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import '../scss/insurance-page.style.scss';
import { geographical, arrowRight, meeting, damage, download, savings } from './images/jpg/otherInsurancepage';
import { LOCALES } from '../../common/constants';
import { getItem } from '../helper/localstorage.helper';
// Insurance coverage icons - using existing assets
const DamageIcon = damage;
const LossIcon = savings;
const GeographicalIcon = geographical;
const MeetingIcon = meeting;
const ArrowRightIcon = arrowRight;
const InsuranceBackground = '/assets/dashboard-pension-banner.png';

interface InsuranceCardProps {
    title: string;
    description: string;
    coverageItems?: string[];
    isLeftCard?: boolean;
}

const InsuranceCard: React.FC<InsuranceCardProps> = ({ title, description, coverageItems, isLeftCard = false }) => {
    return (
        <div className={`insurance-card ${isLeftCard ? 'left-card' : 'right-card'}`}>
            <div className="insurance-card-header">
                <h3 className="insurance-card-title">{title}</h3>
            </div>
            <div className="insurance-card-content">
                {coverageItems ? (
                    <ul className="coverage-list">
                        {coverageItems.map((item, index) => (
                            <li key={index} className="coverage-item">{item}</li>
                        ))}
                    </ul>
                ) : (
                    <p className="insurance-card-description">{description}</p>
                )}
            </div>
        </div>
    );
};

const InsurancePage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const language = i18n.language;
    const user = JSON.parse(getItem('user'));

    const handleContactClick = () => {
        navigate(
            language === LOCALES.EN_US
                ? `/contact-us?lang=${LOCALES.EN_US}`
                : `/kontakta-oss?lang=${LOCALES.SV_SE}`
        );
    };

    const handleCreateAccountClick = () => {
        navigate('/register');
    };

    const handleDownloadInsuranceLetter = () => {
        window.open('https://www.api.invozio.com/img/F%C3%B6rs%C3%A4kringsbrev.pdf?_gl=1*1n1guna*_gcl_au*MTU4ODAwMTk2NS4xNzU0MDI2NDUy*_ga*MTQyNTE0OTA3Mi4xNzU0MDI2NDUy*_ga_DJKEC77RNR*czE3NTU1MDc3NjkkbzI0JGcxJHQxNzU1NTA4MDk3JGo2MCRsMCRoMA..', '_blank');
    };

    return (
        <div className="insurance-page px-5">
            {/* Hero Section */}
            <section className="insurance-hero">
                <div className="container px-0">
                    <div className="insurance-hero-content">
                        <div className="hero-text-parallel">
                            <div className="hero-title-section">
                                <h2 className="hero-title">
                                    {t('INSURANCE_HERO_SUBTITLE')}
                                </h2>
                            </div>
                            <div className="hero-subtitle-section ">
                                <h2 className="hero-subtitle-main">{t('HAVE_YOU_BEEN_IN_ACCIDENT_OR_CAUSED_DAMAGE')}</h2>
                                <p className="hero-description ">
                                    {t('REPORT_INCIDENT_INSTRUCTIONS')} {user && t('USE_THE_FOLLOWING_LINK')}
                                </p>
                                {user && (
                                    <a
                                        className="download-btn d-inline-flex align-items-center trygghansa-link"
                                        href="https://www.trygghansa.se/skadeanmalan/anmal-en-skada/fritext#/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <span className="download-text">{t('REPORT_INCIDENT_TRYGG_HANSA')}</span>
                                        <span className="ms-3 download-icon d-inline-flex align-items-center" aria-hidden>
                                            <svg
                                                width={20}
                                                height={20}
                                                viewBox="0 0 20 20"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M16.665 6.66667C16.665 7.12782 17.0388 7.50167 17.5 7.50167C17.9612 7.50167 18.335 7.12782 18.335 6.66667H17.5H16.665ZM13.3333 1.665C12.8722 1.665 12.4983 2.03884 12.4983 2.5C12.4983 2.96116 12.8722 3.335 13.3333 3.335V2.5V1.665ZM9.40957 9.40957C9.08348 9.73565 9.08348 10.2643 9.40957 10.5904C9.73565 10.9165 10.2643 10.9165 10.5904 10.5904L10 10L9.40957 9.40957ZM17.4546 2.7275L16.7106 3.10658L16.7106 3.10659L17.4546 2.7275ZM17.2725 2.54541L16.8934 3.28941L16.8934 3.28941L17.2725 2.54541ZM10 5.00167C10.4612 5.00167 10.835 4.62782 10.835 4.16667C10.835 3.70551 10.4612 3.33167 10 3.33167V4.16667V5.00167ZM16.6683 10C16.6683 9.53884 16.2945 9.165 15.8333 9.165C15.3722 9.165 14.9983 9.53884 14.9983 10H15.8333H16.6683ZM4.32003 17.1367L4.69911 16.3927L4.32003 17.1367ZM2.86331 15.68L2.11932 16.059L2.86331 15.68ZM14.0133 17.1367L13.6342 16.3927L14.0133 17.1367ZM15.47 15.68L14.726 15.3009H14.726L15.47 15.68ZM2.86331 5.9867L2.11932 5.60762L2.11932 5.60762L2.86331 5.9867ZM4.32003 4.52998L3.94095 3.78599H3.94095L4.32003 4.52998ZM17.5 6.66667H18.335V3.16667H17.5H16.665V6.66667H17.5ZM16.8333 2.5V1.665H13.3333V2.5V3.335H16.8333V2.5ZM17.378 2.62204L16.7875 2.0316L9.40957 9.40957L10 10L10.5904 10.5904L17.9684 3.21247L17.378 2.62204ZM17.5 3.16667H18.335C18.335 3.06377 18.3356 2.93637 18.3265 2.82501C18.3167 2.70389 18.2913 2.53032 18.1986 2.34842L17.4546 2.7275L16.7106 3.10659C16.6875 3.06118 16.6748 3.02078 16.6681 2.99231C16.662 2.96649 16.6614 2.9529 16.6621 2.961C16.6628 2.96941 16.6638 2.98799 16.6644 3.02526C16.665 3.0625 16.665 3.10642 16.665 3.1666717.5ZM16.8333 2.5V3.335C16.8936 3.335 16.9375 3.33502 16.9747 3.33561C17.012 3.33619 17.0306 3.33722 17.039 3.3379C17.0471 3.33856 17.0335 3.33801 17.0077 3.33193C16.9792 3.32522 16.9388 3.31254 16.8934 3.28941L17.2725 2.54541L17.6516 1.80142C17.4697 1.70874 17.2961 1.68335 17.175 1.67345C17.0636 1.66435 16.9362 1.665 16.8333 1.665V2.5ZM17.4546 2.7275L18.1986 2.34843C18.1386 2.23067 18.0607 2.1239 17.9684 2.0316L17.378 2.62204L16.7875 3.21247C16.7567 3.18163 16.7306 3.14594 16.7106 3.10658L17.4546 2.7275ZM17.378 2.62204L17.9684 2.03161C17.8761 1.9393 17.7693 1.86142 17.6516 1.80142L17.2725 2.54541L16.8934 3.28941C16.8541 3.26935 16.8184 3.24332 16.7875 3.21247L17.378 2.62204ZM10.5 17.5V16.665H7.83333V17.5V18.335H10.5V17.5ZM2.5 12.1667H3.335V9.5H2.5H1.665V12.1667H2.5ZM7.83333 4.16667V5.00167H10V4.16667V3.33167H7.83333V4.16667ZM15.8333 10H14.9983V12.1667H15.8333H16.6683V10H15.8333ZM7.83333 17.5V16.665C6.88613 16.665 6.22595 16.6644 5.712 16.6224C5.2078 16.5812 4.9183 16.5044 4.69911 16.3927L4.32003 17.1367L3.94095 17.8807C4.4348 18.1323 4.96853 18.2372 5.57601 18.2868C6.17374 18.3356 6.91369 18.335 7.83333 18.335V17.5ZM2.5 12.1667H1.665C1.665 13.0863 1.66435 13.8263 1.71319 14.424C1.76282 15.0315 1.86769 15.5652 2.11932 16.059L2.86331 15.68L3.6073 15.3009C3.49562 15.0817 3.41884 14.7922 3.37764 14.288C3.33565 13.774 3.335 13.1139 3.335 12.1667H2.5ZM4.32003 17.1367L4.69911 16.3927C4.22902 16.1532 3.84683 15.771 3.6073 15.3009L2.86331 15.68L2.11932 16.059C2.51895 16.8434 3.15663 17.481 3.94095 17.8807L4.32003 17.1367ZM10.5 17.5V18.335C11.4196 18.335 12.1596 18.3356 12.7573 18.2868C13.3648 18.2372 13.8985 18.1323 14.3924 17.8807L14.0133 17.1367L13.6342 16.3927C13.415 16.5044 13.1255 16.5812 12.6213 16.6224C12.1074 16.6644 11.4472 16.665 10.5 16.665V17.5ZM15.8333 12.1667H14.9983C14.9983 13.1139 14.9977 13.774 14.9557 14.288C14.9145 14.7922 14.8377 15.0817 14.726 15.3009L15.47 15.68L16.214 16.059C16.4656 15.5652 16.5705 15.0315 16.6201 14.424C16.669 13.8263 16.6683 13.0863 16.6683 12.1667H15.8333ZM14.0133 17.1367L14.3924 17.8807C15.1767 17.481 15.8144 16.8434 16.214 16.059L15.47 15.68L14.726 15.3009C14.4865 15.771 14.1043 16.1532 13.6342 16.3927L14.0133 17.1367ZM2.5 9.5H3.335C3.335 8.5528 3.33565 7.89262 3.37764 7.37867C3.41884 6.87447 3.49562 6.58497 3.6073 6.36578L2.86331 5.9867L2.11932 5.60762C1.86769 6.10147 1.76282 6.6352 1.71319 7.24268C1.66435 7.84041 1.665 8.58036 1.665 9.5H2.5ZM7.83333 4.16667V3.33167C6.91369 3.33167 6.17374 3.33102 5.57601 3.37985C4.96853 3.42949 4.4348 3.53436 3.94095 3.78599L4.32003 4.52998L4.69911 5.27397C4.9183 5.16229 5.2078 5.0855 5.712 5.04431C6.22595 5.00232 6.88613 5.00167 7.83333 5.00167V4.16667ZM2.86331 5.9867L3.6073 6.36578C3.84683 5.89569 4.22902 5.51349 4.69911 5.27397L4.32003 4.52998L3.94095 3.78599C3.15663 4.18562 2.51895 4.82329 2.11932 5.60762L2.86331 5.9867Z"
                                                    fill="#6750B8"
                                                />
                                            </svg>
                                        </span>
                                    </a>
                                )}
                            </div>

                        </div>
                        <div className="hero-image">
                            <img src={meeting} alt="Insurance hero" />
                        </div>
                    </div>
                </div>
            </section>

            <section className="insurance-hero">
                <div className="container px-0">
                    <div className="insurance-hero-content">
                        <div className="hero-text-parallel">
                            <div className="hero-title-section">
                                <h2 className="section-title">
                                    {t('INSURANCE_HERO_DESCRIPTION')}
                                </h2>
                            </div>
                            <div className="hero-subtitle-section ">
                                <h2 className="hero-subtitle-main pb-5 pb-sm-0"></h2>
                                <p className="hero-description">
                                    {t('INSURANCE_HERO_DESCRIPTION_FULL')}
                                </p>
                                <button className="download-btn" onClick={handleDownloadInsuranceLetter}>
                                    {t('INSURANCE_DOWNLOAD_LETTER')}
                                    <img src={download} alt="Download icon" className="download-icon" />
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            </section>
            {/* Insurance Types Section */}
            <section className="insurance-types">
                <div className="container px-0">
                    <div className="insurance-types-header">
                        <h2 className="section-title ">{t('INSURANCE_TYPES_TITLE')}</h2>
                        <div className="section-content">
                            <p className="section-description">{t('INSURANCE_TYPES_DESCRIPTION')}</p>
                            <h3 className="liability-title">{t('INSURANCE_LIABILITY_TITLE')}</h3>
                            <p className="liability-description">{t('INSURANCE_LIABILITY_DESCRIPTION')}</p>
                            <h3 className="coverage-title">{t('INSURANCE_COVERAGE_TITLE')}</h3>

                            {/* Coverage Icons */}
                            <div className="coverage-icons">
                                <div className="coverage-icon-item">
                                    <div className="coverage-icon">
                                        <img src={DamageIcon} alt="Damage icon" />
                                    </div>
                                    <p className="coverage-text">{t('INSURANCE_COVERAGE_DAMAGE')}</p>
                                </div>
                                <div className="coverage-icon-item">
                                    <div className="coverage-icon">
                                        <img src={LossIcon} alt="Loss icon" />
                                    </div>
                                    <p className="coverage-text">{t('INSURANCE_COVERAGE_LOSS')}</p>
                                </div>
                                <div className="coverage-icon-item">
                                    <div className="coverage-icon">
                                        <img src={GeographicalIcon} alt="Geographical icon" />
                                    </div>
                                    <p className="coverage-text">{t('INSURANCE_COVERAGE_GEOGRAPHICAL')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Comprehensive Coverage Section */}
            <section className="comprehensive-coverage">
                <div className="container px-0">
                    <h2 className="comprehensive-title text-start text-sm-center">{t('INSURANCE_COMPREHENSIVE_TITLE')}</h2>
                </div>
            </section>

            {/* Insurance Cards Grid */}
            <section className="insurance-cards-grid">
                <div className="container px-0">
                    <div className="insurance-cards-row">
                        <InsuranceCard
                            title={t('INSURANCE_ACCIDENT_TITLE')}
                            description={t('INSURANCE_ACCIDENT_DESCRIPTION')}
                            isLeftCard={true}
                        />
                        <InsuranceCard
                            title={t('INSURANCE_ACCIDENT_COVERAGE_TITLE')}
                            description=""
                            coverageItems={[
                                t('INSURANCE_ACCIDENT_COVERAGE_MEDICAL'),
                                t('INSURANCE_ACCIDENT_COVERAGE_WORKING')
                            ]}
                        />
                    </div>

                    <div className="insurance-cards-row">
                        <InsuranceCard
                            title={t('INSURANCE_LEGAL_TITLE')}
                            description={t('INSURANCE_LEGAL_DESCRIPTION')}
                            isLeftCard={true}
                        />
                        <InsuranceCard
                            title={t('INSURANCE_LEGAL_COVERAGE_TITLE')}
                            description=""
                            coverageItems={[
                                t('INSURANCE_LEGAL_COVERAGE_COSTS'),
                                t('INSURANCE_LEGAL_COVERAGE_DEDUCTIBLE')
                            ]}
                        />
                    </div>

                    <div className="insurance-cards-row">
                        <InsuranceCard
                            title={t('INSURANCE_PROPERTY_TITLE')}
                            description={t('INSURANCE_PROPERTY_DESCRIPTION')}
                            isLeftCard={true}
                        />
                        <InsuranceCard
                            title={t('INSURANCE_PROPERTY_COVERAGE_TITLE')}
                            description=""
                            coverageItems={[
                                t('INSURANCE_PROPERTY_COVERAGE_LOSSES'),
                                t('INSURANCE_PROPERTY_COVERAGE_DEDUCTIBLE')
                            ]}
                        />
                    </div>

                    <div className="insurance-cards-row">
                        <InsuranceCard
                            title={t('INSURANCE_CRISIS_TITLE')}
                            description={t('INSURANCE_CRISIS_DESCRIPTION')}
                            isLeftCard={true}
                        />
                        <InsuranceCard
                            title={t('INSURANCE_CRISIS_COVERAGE_TITLE')}
                            description=""
                            coverageItems={[
                                t('INSURANCE_CRISIS_COVERAGE_SESSIONS'),
                                t('INSURANCE_CRISIS_COVERAGE_DEDUCTIBLE')
                            ]}
                        />
                    </div>

                    <div className="insurance-cards-row">
                        <InsuranceCard
                            title={t('INSURANCE_TRAVEL_TITLE')}
                            description={t('INSURANCE_TRAVEL_DESCRIPTION')}
                            isLeftCard={true}
                        />
                        <InsuranceCard
                            title={t('INSURANCE_TRAVEL_COVERAGE_TITLE')}
                            description=""
                            coverageItems={[
                                t('INSURANCE_TRAVEL_COVERAGE_BELONGINGS'),
                                t('INSURANCE_TRAVEL_COVERAGE_VALUABLE'),
                                t('INSURANCE_TRAVEL_COVERAGE_MEDICAL'),
                                t('INSURANCE_TRAVEL_COVERAGE_LEGAL')
                            ]}
                        />
                    </div>
                </div>
            </section>

            {/* Why Important Section */}
            <section className="why-important">
                <div className="container px-0">
                    <div className="why-important-content">
                        <h2 className="section-title ">{t('INSURANCE_WHY_IMPORTANT_TITLE')}</h2>
                        <div className="section-content">
                            <p className="section-description">{t('INSURANCE_WHY_IMPORTANT_DESCRIPTION')}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* How to Benefit Section */}
            <section className="how-to-benefit">
                <div className="container px-0">
                    <div className="how-to-benefit-content">
                        <h2 className="section-title">{t('INSURANCE_HOW_BENEFIT_TITLE')}</h2>
                        <div className="section-content">
                            <p className="section-description">{t('INSURANCE_HOW_BENEFIT_DESCRIPTION')}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Security Section */}
            <section className="security-section">
                <div className="container px-0">
                    <div className="security-content">
                        <h2 className="section-title">{t('INSURANCE_SECURITY_TITLE')}</h2>
                        <div className="section-content">
                            <p className="section-description">{t('INSURANCE_SECURITY_DESCRIPTION')}</p>
                            <button className="contact-btn" onClick={handleContactClick}>
                                {t('INSURANCE_CONTACT_BUTTON')}
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default InsurancePage;
